/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * @author Gladson Samuel S
 * CourseHeaderStyles.js
 * Styles for difficult topic components
 */

import { styled } from '@mui/material/styles';
import { Button, ButtonBase, IconButton, MenuItem, TextField, Typography } from '@mui/material';
import Paper from '@mui/material/Paper';
import MenuList from '@mui/material/MenuList';
import CustomCard from '../../common/CustomCard';
import Icon from '../../icons/Icon';
import mixins from '../../../assets/styles/mixins.scss';
import CommonStyles from '../../../assets/styles/CommonStyles';
import { UNIFIED_COURSE_CARD_CLASSES } from '../../../common/constants';

export const CourseHeaderContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== 'twoColumns'
})(({ twoColumns }) => ({
  '&&': {
    display: 'flex',
    flexDirection: 'column',
    gap: twoColumns ? 16 : 12,

    '& > *': {
      boxSizing: 'border-box'
    },
    [mixins['md-sm-min']]: {
      flexDirection: 'row'
    },
    ...(twoColumns ? {
      backgroundColor: mixins.lightThemeBgColor2,
      padding: 16,
      borderRadius: 24,
      boxSizing: 'border-box',

      [mixins['mlg-min']]: {
        flexDirection: 'row',
        alignItems: 'stretch'
      },

      '& > *:last-child': {
        flex: '1 0 0',
        width: '100%',
        backgroundColor: `${mixins.chalkWhiteFont} !important`,
        borderRadius: '16px !important',
        padding: '12px !important',
        flexDirection: 'column',
        gap: 8,
        alignItems: 'flex-start',

        [mixins['md-sm-min']]: {
          flexDirection: 'row'
        },

        [mixins['mlg-min']]: {
          flex: '1 1 0',
          minWidth: 0,
          width: 'auto'
        },

        '& > div:first-child': {
          [mixins['mlg-min']]: {
            minWidth: '128.25px',
            width: '128.25px',
            height: '171px',
            maxHeight: '171px'
          },
          [mixins['lg-min']]: {
            minWidth: '135px',
            width: '135px',
            height: '180px',
            maxHeight: '180px'
          },

          '& img': {
            [mixins['mlg-min']]: {
              width: '128.25px',
              height: '171px'
            },
            [mixins['lg-min']]: {
              width: '135px',
              height: '180px'
            }
          }
        }
      }
    } : {})
  }
}));

export const CourseHeaderCard = styled(CustomCard)({
  '&&': {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 4,
    flex: '1 0 0',
    alignSelf: 'stretch',
    borderRadius: '12px',
    backgroundColor: mixins.chalkWhiteFont
  }
});

export const CourseHeaderCourseCard = styled(CourseHeaderCard)({
  '&&': {
    backgroundColor: mixins.lightThemeBgColor2,
    borderRadius: 24
  }
});

export const CourseHeaderCourseCardTwoColumns = styled(CourseHeaderCard)({
  '&&': {
    backgroundColor: mixins.chalkWhiteFont,
    borderRadius: 16,
    padding: 12,
    gap: 12,
    flexDirection: 'column',
    justifyContent: 'space-between',
    flex: '1 0 0',
    width: '100%',

    [mixins['mlg-min']]: {
      flex: '0 0 416px',
      maxWidth: '416px'
    }
  }
});

export const CourseHeaderCourseCardContent = styled('div')({
  '&&': {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    width: '100%',
    flex: '1 0 0',
    padding: 4,

    '& > .MuiTypography-root': {
      fontSize: '24px',
      lineHeight: '32px',
      letterSpacing: '-0.48px',

      [mixins['mlg-min']]: {
        fontSize: '24px',
        lineHeight: '32px',
        letterSpacing: '-0.48px'
      },
      [mixins['lg-min']]: {
        fontSize: '24px',
        lineHeight: '32px',
        letterSpacing: '-0.48px'
      }
    }
  }
});

export const CourseHeaderMetaDataContainer = styled('div')({
  '&&': {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 4,
    alignItems: 'center',
    width: '100%'
  }
});

export const CourseHeaderMetaDataItem = styled('div')({
  '&&': {
    display: 'inline-flex',
    alignItems: 'center',
    padding: 4,
    borderRadius: 4,
    border: '1px solid rgba(13, 0, 77, 0.45)',
    backgroundColor: mixins.chalkWhiteFont,
    whiteSpace: 'nowrap'
  }
});

export const CourseHeaderMetaDataLabel = styled('span')({
  '&&': {
    color: mixins.color_text_primary,
    fontFeatureSettings: '\'liga\' off',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight400,
    lineHeight: '22px'
  }
});

export const CourseHeaderMetaDataValue = styled('span')({
  '&&': {
    color: mixins.color_text_primary,
    fontFeatureSettings: '\'liga\' off',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight600,
    lineHeight: '22px'
  }
});

export const CourseHeaderInviteStudentsButton = styled(Button)({
  '&&': {
    color: mixins.amethyst,
    fontFeatureSettings: '\'liga\' off',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight600,
    lineHeight: '24px',
    textTransform: 'none',
    padding: 0,
    minWidth: 'unset',
    backgroundColor: 'transparent',

    '&:hover': {
      backgroundColor: 'transparent',
      color: mixins.linkHoverColor,
      textDecoration: 'underline'
    }
  }
});

export const CourseHeaderCourseChildrenContent = styled('div')({
  '&&': {
    marginTop: 16,
    width: '100%'
  }
});

export const CourseHeaderCourseTitleContainer = styled('div')({
  '&&': {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    alignItems: 'baseline',
    gap: 4,

    [mixins['md-sm-min']]: {
      alignItems: 'flex-start'
    }
  }
});

export const CourseHeaderCourseTitle = styled(Typography)({
  '&&': {
    color: mixins.color_text_primary,
    fontFeatureSettings: '\'liga\' off',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: '24px',
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight500,
    lineHeight: '30px',
    wordBreak: 'break-word',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    [mixins['mlg-min']]: {
      fontSize: '28px',
      lineHeight: '35px',
      letterSpacing: '-0.54px'
    },
    [mixins['lg-min']]: {
      fontSize: '32px',
      lineHeight: '40px',
      letterSpacing: '-0.64px'
    }
  }
});

export const CourseHeaderBookTitle = styled(Typography)({
  '&&': {
    color: mixins.color_text_primary,
    fontFeatureSettings: '\'liga\' off',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: '20px',
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight500,
    lineHeight: '27px',

    [mixins['mlg-min']]: {
      fontSize: '24px',
      lineHeight: '29px',
      letterSpacing: '-0.4px'
    },
    [mixins['xl-min']]: {
      fontSize: '24px',
      lineHeight: '32px',
      letterSpacing: '-0.4px'
    }
  }
});

export const CourseHeaderInstructorContainer = styled('div')({
  '&&': {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  }
});

export const CourseHeaderAvatar = styled('div')({
  '&&': {
    display: 'flex',
    width: '40px',
    height: '40px',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '50%',
    color: mixins.color_text_primary,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight500,
    lineHeight: '22px',

    '&:has(span)': {
      border: `2px solid ${mixins.navyColor}`
    },
    '& svg': {
      height: '100%',
      width: '100%',
      color: mixins.navyColor
    }
  }
});

export const CourseHeaderAvatarImage = styled('img')({
  '&&': {
    display: 'flex',
    width: '40px',
    height: '40px',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '50%',
    color: mixins.color_text_primary,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight500,
    lineHeight: '22px'
  }
});

export const CourseHeaderInstructorName = styled(Typography)({
  '&&': {
    color: mixins.color_text_primary,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight500,
    lineHeight: '22px'
  }
});

export const CourseHeaderInfoContainer = styled('div')({
  '&&': {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    flexGrow: 1,
    alignItems: 'flex-end',
    marginTop: 4
  }
});

export const CourseHeaderActionsContainer = styled('div')({
  '&&': {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',

    [mixins['md-sm-min']]: {
      width: 'auto'
    }
  }
});

export const CourseHeaderSecondaryButton = styled(Button)({
  '&&': {
    ...CommonStyles().secondaryButton,
    ...CommonStyles().secondaryButtonText,
    ...CommonStyles().focusVisible,
    maxWidth: '100%',
    width: '100%',
    justifyContent: 'center',
    padding: '8px 16px 10px 16px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',

    [mixins['md-sm-min']]: {
      maxWidth: '145px'
    }
  }
});

export const CourseHeaderSecondaryGuidesButton = styled(Button)({
  '&&': {
    ...CommonStyles().secondaryButton,
    ...CommonStyles().secondaryButtonText,
    ...CommonStyles().focusVisible,
    border: 'none',
    padding: 0,
    height: 'inherit',
    maxHeight: '22px',
    fontWeight: 600,
    color: mixins.amethyst,
    gap: 0,

    '&:hover': {
      background: 'none',
      color: mixins.linkHoverColor
    },
    [mixins['md-sm-min']]: {
      flexGrow: '1',
      alignSelf: 'center'
    }
  }
});

export const CourseHeaderIconButton = styled(IconButton)({
  '&&': {
    ...CommonStyles().iconButton,
    border: '1px solid rgba(13, 0, 77, 0.15)',
    position: 'relative'
  }
});

export const CourseHeaderHotspot = styled('div')({
  '&&': {
    width: 6,
    height: 6,
    backgroundColor: mixins.accentHotspotColor,
    position: 'absolute',
    top: 0,
    right: 5,
    borderRadius: '50%'
  }
});

export const CourseHeaderBookCard = styled(CourseHeaderCard)({
  '&&': {
    flexDirection: 'row',
    padding: '8px !important',
    gap: 12,
    borderRadius: 16,

    [mixins['md-sm-min']]: {
      padding: '12px !important'
    }
  }
});

export const CourseHeaderCoverImageContainer = styled('div')({
  '&&': {
    minWidth: '108px',
    maxHeight: '144px',

    [mixins['md-sm-min']]: {
      minWidth: '109.5px',
      maxHeight: '146px'
    },
    [mixins['mlg-min']]: {
      minWidth: '90.75px',
      maxHeight: '121px'
    },
    [mixins['lg-min']]: {
      minWidth: '93px',
      maxHeight: '124px'
    }
  }
});

export const CourseHeaderCoverImage = styled('img')({
  '&&': {
    width: '108px',
    height: '144px',
    flexShrink: '0',
    aspectRatio: '3/4',
    objectFit: 'cover',
    borderRadius: 8,

    [mixins['md-sm-min']]: {
      width: '109.5px',
      height: '146px'
    },
    [mixins['mlg-min']]: {
      width: '90.75px',
      height: '121px'
    },
    [mixins['lg-min']]: {
      width: '93px',
      height: '124px'
    }
  }
});

export const CourseHeaderDownloadRoot = styled('div')({
  '&&': {
    width: '108px',
    height: '144px',
    flexShrink: '0',
    aspectRatio: '3/4',
    objectFit: 'cover',
    borderRadius: 8,

    [mixins['md-sm-min']]: {
      width: '109.5px',
      height: '146px'
    },
    [mixins['mlg-min']]: {
      width: '90.75px',
      height: '121px'
    },
    [mixins['lg-min']]: {
      width: '93px',
      height: '124px'
    }
  }
});

export const CourseHeaderAuthor = styled(Typography)({
  '&&': {
    color: mixins.zambezi,
    fontFeatureSettings: '\'liga\' off',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight400,
    lineHeight: '22px',
    marginBottom: 8,

    [mixins['mlg-min']]: {
      marginBottom: 0
    }
  }
});

export const CourseHeaderBookInfo = styled('div')({
  '&&': {
    display: 'flex',
    padding: '4px',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 2,
    flex: '1 0 0',
    alignSelf: 'stretch'
  }
});

export const CourseHeaderGoToPageContainer = styled('div')({
  '&&': {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    width: '100%',

    '& .MuiInputBase-input::placeholder': {
      fontSize: 10
    },

    [mixins['md-sm-min']]: {
      flexDirection: 'row',
      alignItems: 'center',
      maxHeight: 40,
      gap: '8px',
      width: 'auto'
    }
  }
});

export const CourseHeaderGoToPageLabel = styled(Typography)({
  '&&': {
    color: mixins.zambezi,
    fontFeatureSettings: '\'liga\' off',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: 12,
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight400,
    lineHeight: '20px'
  }
});

export const CourseHeaderGoToTextField = styled(TextField)({
  '&&': {
    maxHeight: 40,
    width: '100%',
    padding: '8px 12px 8px 12px',
    border: `1px solid ${mixins.amethyst}`,
    borderRadius: 4,
    backgroundColor: mixins.chalkWhiteFont,
    boxSizing: 'border-box',
    maxWidth: '100%',

    '& .MuiInputBase-root': {
      width: '100%',
      color: mixins.greyScaleColor,
      fontFeatureSettings: '\'liga\' off',
      fontFamily: mixins.fontFamilyPlusSans,
      fontSize: mixins.fontSize16,
      fontStyle: 'normal',
      fontWeight: mixins.fontWeight400,
      lineHeight: '24px',

      '&::before, &::after': {
        display: 'none'
      }
    },
    '& .MuiInputBase-input': {
      padding: 0,
      height: 'inherit',
      textAlign: 'center'
    },
    '&:focus-within': {
      outline: `1px solid ${mixins.amethyst}`,
      marginLeft: 2,

      '& .MuiInputBase-root': {
        color: mixins.color_text_primary
      }
    },

    [mixins['md-sm-min']]: {
      maxWidth: '80px'
    }
  }
});

export const CourseHeaderBookActionsContainer = styled('div')({
  '&&': {
    display: 'flex',
    gap: '12px',
    width: '100%',

    [mixins['md-sm-min']]: {
      height: '100%',
      alignItems: 'flex-end'
    }
  }
});

export const CourseHeaderBookActions = styled('div')({
  '&&': {
    display: 'flex',
    width: '100%',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    flexGrow: 1,
    gap: 12,
    flexDirection: 'column',

    [mixins['md-sm-min']]: {
      flexDirection: 'row'
    }
  }
});

export const CourseHeaderScheduleContainer = styled('div')({
  '&&': {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  }
});

export const CourseHeaderScheduleTitle = styled(Typography)({
  '&&': {
    color: mixins.zambezi,
    fontFeatureSettings: '\'liga\' off',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: 12,
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight400,
    lineHeight: '20px'
  }
});

export const CourseHeaderScheduleItem = styled('div')({
  '&&': {
    display: 'flex'
  }
});

export const CourseHeaderScheduleItemTitle = styled(Typography)({
  '&&': {
    display: 'flex',
    padding: '4px 8px',
    color: mixins.color_text_primary,
    fontFeatureSettings: '\'liga\' off',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: 12,
    fontStyle: 'normal',
    fontWeight: 700,
    lineHeight: '20px',
    whiteSpace: 'nowrap',
    border: '1px solid rgba(13, 0, 77, 0.15)',
    borderRadius: 4,
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0
  }
});

export const CourseHeaderScheduleItemsContainer = styled('div')({
  '&&': {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap'
  }
});

export const CourseHeaderScheduleItemTime = styled(Typography)({
  '&&': {
    display: 'flex',
    padding: '4px 8px',
    color: mixins.color_text_primary,
    fontFeatureSettings: '\'liga\' off',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: 12,
    fontStyle: 'normal',
    fontWeight: 700,
    lineHeight: '20px',
    whiteSpace: 'nowrap',
    border: '1px solid rgba(13, 0, 77, 0.15)',
    borderRadius: 4,
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderLeftWidth: 0
  }
});

export const CourseHeaderTitleInfoContainer = styled('div')({
  '&&': {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    width: '100%',
    justifyContent: 'space-between'
  }
});

export const CourseHeaderCourseMenuIcon = styled(CourseHeaderIconButton, {
  shouldForwardProp: (prop) => prop !== 'isMenuOpen'
})(({ isMenuOpen }) => ({
  '&&': {
    padding: 4,
    border: 'none',
    width: 24,
    height: 24,
    transition: 'transform 0.3s ease-in-out',
    marginLeft: 'auto',
    justifyContent: 'flex-end',
    transform: isMenuOpen ? 'rotate(-90deg)' : undefined,

    [mixins['md-sm-min']]: {
      marginTop: 6
    },

    '& > span, & svg': {
      width: 16,
      height: 16
    }
  }
}));

export const CourseHeaderMenuPaper = styled(Paper)({
  '&&': {
    position: 'absolute',
    width: 240,
    minWidth: 240,
    maxWidth: 'calc(100% - 32px)',
    outline: 0,
    backgroundColor: mixins.chalkWhiteFont,
    opacity: 1,
    borderRadius: 8,
    marginTop: 0,
    overflow: 'visible',
    boxShadow: '0px 5px 5px -3px rgba(0, 0, 0, 0.2), 0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 3px 14px 2px rgba(0, 0, 0, 0.12)',
    boxSizing: 'border-box',

    '& *': {
      boxSizing: 'border-box'
    }
  }
});

export const CourseHeaderMenuList = styled(MenuList)({
  '&&': {
    padding: '8px !important',
    width: '100%',
    boxSizing: 'border-box'
  }
});

export const CourseHeaderMenuItem = styled(MenuItem, {
  shouldForwardProp: (prop) => prop !== 'hasDivider'
})(({ hasDivider }) => ({
  '&&': {
    padding: '8px !important',
    minHeight: 'unset',
    width: '100%',
    boxSizing: 'border-box',
    ...(hasDivider ? { borderTop: `1px solid ${mixins.color_elevation_alpha_12}` } : {}),

    '& p': {
      color: mixins.timeIconColor,
      fontFeatureSettings: '\'liga\' off',
      fontFamily: mixins.fontFamilyPlusSans,
      fontSize: mixins.fontSize14,
      fontStyle: 'normal',
      fontWeight: mixins.fontWeight400,
      lineHeight: '22px'
    },

    '&:hover': {
      backgroundColor: mixins.quaternary
    }
  }
}));

export const CourseHeaderIcon = styled(Icon)({
  '&&': {
    width: 16,
    height: 16,
    fontSize: 16,
    display: 'inline-flex',

    '& .MuiSvgIcon-root': {
      width: 16,
      height: 16,
      fontSize: 16
    }
  }
});

export const CourseHeaderIconCtaButton = styled(Button)({
  '&&': {
    color: mixins.navyColor,
    fontFeatureSettings: '\'liga\' off',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight500,
    lineHeight: '24px',
    padding: '8px 16px 10px 12px',
    maxHeight: 40,
    borderRadius: '1000px',
    minWidth: 'unset',
    height: 'fit-content',
    whiteSpace: 'nowrap',
    textTransform: 'none',
    border: 'none',

    '&:hover': {
      background: mixins.secondaryButtonHoverColor
    },
    '& .MuiButton-startIcon': {
      marginRight: 4,
      marginTop: 1,
      marginLeft: 0,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    '& .MuiSvgIcon-root': {
      width: 16,
      height: 16,
      fontSize: 16
    },
    '&.Mui-disabled': {
      color: mixins.disabledTextColor,
      opacity: 'unset',
      pointerEvents: 'unset',

      '&:hover': {
        background: mixins.secondaryButtonHoverColor,
        color: 'inherit'
      }
    }
  }
});

export const CourseHeaderActionContainer = styled('div')({
  '&&': {
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'column',
    alignItems: 'flex-start',
    [mixins['md-sm-min']]: {
      width: '100%',
      justifyContent: 'space-between',
      flexWrap: 'nowrap',
      flexDirection: 'row',
      alignItems: 'unset'
    }
  }
});

export const CourseHeaderFooterContainer = styled('div')({
  '&&': {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    alignItems: 'center',
    marginTop: 'auto',
    paddingTop: 0,

    '& .MuiIconButton-root': {
      width: 24,
      height: 24,
      minWidth: 24,
      padding: 4,
      borderRadius: '1000px',
      border: 'none',
      marginTop: 0,
      backgroundColor: 'transparent',

      '&:hover': {
        backgroundColor: mixins.secondaryButtonHoverColor
      },

      '& > span, & svg': {
        width: 16,
        height: 16
      }
    }
  }
});

export const CourseHeaderButton = styled(Button)({
  '&&': {}
});

/*
 * UnifiedCourseCard (Nebula hero card)
 * Figma: PCUX-103 "By_The_Button" — Desktop ≥1024, Tablet ≥768, Mobile Large ≥480, Mobile Small <480.
 */

const UNIFIED_CARD_RADIUS = 24;
const UNIFIED_INTERACTIVE_HOVER_BG = 'rgba(13, 0, 77, 0.08)';
const UNIFIED_CHIP_BG = '#F6F6FB';
const UNIFIED_CHIP_TEXT = '#2E2E2E';
const UNIFIED_BADGE_BG = '#EFEDFF';

/**
 * Lifts an interactive slot above the title's stretched hit-area so it stays
 * independently clickable inside a clickable card.
 */
const unifiedAboveHitArea = {
  position: 'relative',
  zIndex: 1
};

const unifiedFocusRing = {
  outline: `2px solid ${mixins.secondaryButtonFocusColor}`,
  outlineOffset: 2
};

export const CourseHeaderUnifiedRoot = styled('div', {
  shouldForwardProp: (prop) => prop !== 'hasCover' && prop !== 'isClickable'
})(({ hasCover, isClickable }) => ({
  '&&': {
    position: 'relative',
    display: 'grid',
    gridTemplateColumns: hasCover ? '120px minmax(0, 1fr)' : 'minmax(0, 1fr)',
    gridTemplateAreas: hasCover ? '"heading heading" "cover details"' : '"heading" "details"',
    rowGap: 12,
    width: '100%',
    padding: 12,
    boxSizing: 'border-box',
    backgroundColor: mixins.chalkWhiteFont,
    borderRadius: UNIFIED_CARD_RADIUS,
    transition: 'box-shadow 150ms ease-in-out',

    '& *': {
      boxSizing: 'border-box'
    },

    ...(isClickable ? {
      cursor: 'pointer',

      // Only when the pointer is on the card surface, not on the buttons above it.
      [`&:has(.${UNIFIED_COURSE_CARD_CLASSES.titleButton}:hover)`]: {
        boxShadow: '0px 4px 12px rgba(13, 0, 77, 0.12), 0px 1px 3px rgba(13, 0, 77, 0.08)'
      }
    } : {}),

    [mixins['sml-min']]: {
      gridTemplateAreas: hasCover ? '"cover content"' : '"content"',
      rowGap: 0,
      alignItems: 'start'
    },

    [mixins['md-sm-min']]: {
      gridTemplateColumns: hasCover ? '144px minmax(0, 1fr)' : 'minmax(0, 1fr)',
      alignItems: 'center'
    }
  }
}));

export const CourseHeaderUnifiedCoverContainer = styled('div')({
  '&&': {
    gridArea: 'cover',
    position: 'relative',
    width: 120,
    height: 160,
    flexShrink: 0,

    [mixins['md-sm-min']]: {
      width: 144,
      height: 192
    }
  }
});

export const CourseHeaderUnifiedCoverImage = styled('img')({
  '&&': {
    display: 'block',
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    borderRadius: 12
  }
});

export const CourseHeaderUnifiedDownloadRoot = styled('div')({
  '&&': {
    ...unifiedAboveHitArea,
    width: '100%',
    height: '100%',
    borderRadius: 12,
    overflow: 'hidden',

    '& > div': {
      width: '100%',
      height: '100%',
      borderRadius: 12
    }
  }
});

export const CourseHeaderUnifiedCoverInfoButton = styled(IconButton)({
  '&&': {
    ...unifiedAboveHitArea,
    position: 'absolute',
    top: 4,
    left: 4,
    zIndex: 2,
    width: 32,
    height: 32,
    minWidth: 32,
    padding: 6,
    borderRadius: '1000px',
    backgroundColor: mixins.chalkWhiteFont,
    color: mixins.color_text_primary,

    '&:hover': {
      backgroundColor: mixins.lightThemeBgColor2
    },
    '&.Mui-focusVisible, &:focus-visible': unifiedFocusRing,

    '& svg, & .MuiSvgIcon-root': {
      width: 20,
      height: 20,
      fontSize: 20
    }
  }
});

/**
 * Below 480px the heading and details become direct grid items (heading spans the
 * full width above the cover); from 480px up they stack in one column beside it.
 */
export const CourseHeaderUnifiedContent = styled('div')({
  '&&': {
    display: 'contents',

    [mixins['sml-min']]: {
      gridArea: 'content',
      display: 'flex',
      flexDirection: 'column',
      minWidth: 0
    }
  }
});

export const CourseHeaderUnifiedHeading = styled('div')({
  '&&': {
    gridArea: 'heading',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 8,
    minWidth: 0,

    [mixins['sml-min']]: {
      padding: '12px 24px 0'
    }
  }
});

export const CourseHeaderUnifiedCourseTitle = styled(Typography)({
  '&&': {
    margin: 0,
    width: '100%',
    color: mixins.color_text_primary,
    fontFeatureSettings: '\'liga\' off',
    fontFamily: mixins.fontFamilyPlusSans,
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight500,
    fontSize: 22,
    lineHeight: '28px',
    letterSpacing: '-0.44px',
    wordBreak: 'break-word',

    [mixins['md-sm-min']]: {
      fontSize: 26,
      lineHeight: '34px',
      letterSpacing: '-0.52px'
    },

    [mixins['mlg-min']]: {
      fontSize: 28,
      lineHeight: '36px',
      letterSpacing: '-0.56px'
    }
  }
});

/**
 * The card's single click target. Its ::after stretches over the whole card, so
 * the full card is clickable while keyboard / screen-reader users get one button.
 */
export const CourseHeaderUnifiedTitleButton = styled('button')({
  '&&': {
    display: 'inline',
    // Negative margin keeps the text aligned while the hover background bleeds 4px.
    padding: '0 4px',
    margin: '0 -4px',
    border: 0,
    borderRadius: 4,
    backgroundColor: 'transparent',
    boxDecorationBreak: 'clone',
    WebkitBoxDecorationBreak: 'clone',
    font: 'inherit',
    letterSpacing: 'inherit',
    color: 'inherit',
    textAlign: 'left',
    cursor: 'pointer',
    transition: 'background-color 150ms ease-in-out',

    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      zIndex: 0,
      borderRadius: UNIFIED_CARD_RADIUS
    },

    '&:hover': {
      backgroundColor: UNIFIED_INTERACTIVE_HOVER_BG
    },

    '&:focus-visible': {
      outline: 'none',

      '&::after': unifiedFocusRing
    }
  }
});

export const CourseHeaderUnifiedMetaRow = styled('div')({
  '&&': {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    gap: 8,
    width: '100%'
  }
});

const unifiedChip = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: 8,
  borderRadius: 4,
  fontFeatureSettings: '\'liga\' off',
  fontFamily: mixins.fontFamilyPlusSans,
  fontSize: mixins.fontSize14,
  fontStyle: 'normal',
  lineHeight: '16px',
  whiteSpace: 'nowrap',
  flexShrink: 0
};

export const CourseHeaderUnifiedChip = styled('span')({
  '&&': {
    ...unifiedChip,
    backgroundColor: UNIFIED_CHIP_BG,
    color: UNIFIED_CHIP_TEXT,
    fontWeight: mixins.fontWeight500
  }
});

export const CourseHeaderUnifiedBadge = styled('span')({
  '&&': {
    ...unifiedChip,
    backgroundColor: UNIFIED_BADGE_BG,
    color: mixins.amethyst,
    fontWeight: mixins.fontWeight600
  }
});

export const CourseHeaderUnifiedDetails = styled('div')({
  '&&': {
    gridArea: 'details',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    gap: 12,
    minWidth: 0,
    padding: '8px 12px',

    [mixins['sml-min']]: {
      justifyContent: 'flex-start',
      gap: 8,
      padding: '16px 12px 12px'
    }
  }
});

export const CourseHeaderUnifiedBookTitle = styled(Typography)({
  '&&': {
    margin: 0,
    padding: '0 12px',
    width: '100%',
    color: mixins.zambezi,
    fontFeatureSettings: '\'liga\' off',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight600,
    lineHeight: '24px',
    wordBreak: 'break-word',
    ...CommonStyles().truncateNumberOfLines(4)
  }
});

export const CourseHeaderUnifiedActions = styled('div')({
  '&&': {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 16,
    width: '100%'
  }
});

export const CourseHeaderUnifiedBookActionButton = styled(CourseHeaderIconCtaButton)({
  '&&': unifiedAboveHitArea
});

export const CourseHeaderUnifiedSecondaryButton = styled(CourseHeaderSecondaryButton)({
  '&&': {
    ...unifiedAboveHitArea,
    width: 'auto'
  }
});

export const CourseHeaderUnifiedLmsStatus = styled('span')({
  '&&': {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '0 12px',
    color: mixins.zambezi,
    fontFeatureSettings: '\'liga\' off',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize12,
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight400,
    lineHeight: '20px',
    whiteSpace: 'nowrap',

    '& .MuiSvgIcon-root': {
      width: 16,
      height: 16,
      fontSize: 16
    }
  }
});

export const CourseHeaderUnifiedInviteStudentsButton = styled(CourseHeaderInviteStudentsButton)({
  '&&': {
    ...unifiedAboveHitArea,
    padding: '4px 12px',
    borderRadius: 4,

    '&.Mui-focusVisible, &:focus-visible': unifiedFocusRing
  }
});

export const CourseHeaderUnifiedSettingsGroup = styled('div')({
  '&&': {
    display: 'inline-flex',
    alignItems: 'center'
  }
});

export const CourseHeaderUnifiedIconButton = styled(ButtonBase)({
  '&&': {
    ...unifiedAboveHitArea,
    width: 32,
    height: 32,
    padding: 0,
    border: 0,
    borderRadius: 4,
    color: mixins.navyColor,
    transition: 'background-color 150ms ease-in-out',

    '& .MuiSvgIcon-root': {
      width: 18,
      height: 18,
      fontSize: 18
    },
    '&:hover': {
      backgroundColor: UNIFIED_INTERACTIVE_HOVER_BG
    },
    '&.Mui-focusVisible, &:focus-visible': unifiedFocusRing
  }
});
